import React, { useState, useEffect, useMemo } from "react";
import { 
  Package, 
  Calendar, 
  Gamepad2, 
  StickyNote, 
  Plus, 
  X, 
  RefreshCw, 
  Loader2, 
  Search, 
  Database, 
  Image, 
  Tag,
  Layers,
  Trophy,
  Clock,
  AlertTriangle,
  Edit2,
  Share2,
  ListTodo,
  Trash2,
  CheckSquare,
  Copy,
  Bold,
  Italic,
  Lock,
  Eye,
  ChevronRight,
  Paperclip,
  UserCircle2,
  ArrowUpDown,
  Send,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import GameRequestDialog from "@/components/GameRequestDialog";
import { getMinValidDate, isValidUnlockDate } from "@/utils/dateUtils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { CalendarWithYear } from "@/components/ui/calendar-with-year";
import { format } from "date-fns";
import { TROPHY_TYPES, formatTime12Hour } from "./AchievementCard";
import { getTagColor } from "@/utils/tagColors";

// Auto-format date input: adds "/" between MM/DD/YYYY as user types
// Kept as a thin re-export for callers importing from this module.
import { formatDateInput, formatTimeInput } from "@/utils/dateUtils";
export { formatDateInput, formatTimeInput };


// Edit Achievement Dialog
export function EditAchievementDialog({
  open,
  onOpenChange,
  achievement,
  setAchievement,
  dlcs,
  sections = [],
  tags,
  platform,
  onSave,
  toggleTag,
}) {
  if (!achievement) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-lg max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Edit Achievement</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-4 overflow-y-auto max-h-[65vh] pr-2">
          <div className="space-y-2">
            <Label>Name</Label>
            <Input
              value={achievement.name}
              onChange={(e) => setAchievement({ ...achievement, name: e.target.value })}
              className="bg-black/50 border-white/10"
            />
          </div>

          {/* Move to DLC/Content selector */}
          {dlcs.length > 0 && (
            <div className="space-y-2 p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
              <Label className="flex items-center gap-2">
                <Package className="w-4 h-4 text-purple-400" />
                Move to Content
              </Label>
              <Select 
                value={achievement.dlcId || "base"} 
                onValueChange={(v) => setAchievement({ ...achievement, dlcId: v, sectionId: v === "base" ? achievement.sectionId : null })}
              >
                <SelectTrigger className="bg-black/50 border-white/10">
                  <SelectValue placeholder="Select content" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  <SelectItem value="base">Base Game</SelectItem>
                  {dlcs.map(dlc => (
                    <SelectItem key={dlc.id} value={dlc.id}>{dlc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Move to Section/Organizer selector - show for all achievements */}
          {sections.length > 0 && (
            <div className="space-y-2 p-3 bg-cyan-500/10 border border-cyan-500/20 rounded-lg">
              <Label className="flex items-center gap-2">
                <Layers className="w-4 h-4 text-cyan-400" />
                Move to Organizer
              </Label>
              <Select 
                value={achievement.sectionId || "unsectioned"} 
                onValueChange={(v) => setAchievement({
                  ...achievement,
                  sectionId: v === "unsectioned" ? null : v,
                  // Changing organizer always resets the subgroup — it belongs
                  // to the previous organizer and is meaningless in the new one.
                  subSectionId: null,
                })}
              >
                <SelectTrigger className="bg-black/50 border-white/10">
                  <SelectValue placeholder="Select organizer" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  <SelectItem value="unsectioned">
                    {achievement.dlcId && achievement.dlcId !== "base" ? "Back to DLC (no organizer)" : "Unsectioned (Base Game)"}
                  </SelectItem>
                  {[...sections].sort((a, b) => a.name.localeCompare(b.name)).map(section => (
                    <SelectItem key={section.id} value={section.id}>{section.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {achievement.dlcId && achievement.dlcId !== "base" && achievement.sectionId && (
                <p className="text-xs text-cyan-400/70 mt-1">
                  This DLC achievement will appear in the organizer with a DLC tag
                </p>
              )}

              {/* Subgroup picker — nested inside the chosen organizer. The
                  dropdown is greyed out unless the current organizer actually
                  has subgroups defined, so users can see the option exists
                  without pretending it's populated. */}
              {(() => {
                const currentSection = achievement.sectionId
                  ? sections.find((s) => s.id === achievement.sectionId)
                  : null;
                const subs = currentSection?.subsections || [];
                const disabled = !currentSection || subs.length === 0;
                let hint;
                if (!currentSection) {
                  hint = "Assign an organizer above to unlock subgroups.";
                } else if (subs.length === 0) {
                  hint = `\"${currentSection.name}\" has no subgroups yet — create some from the organizer menu.`;
                }
                return (
                  <div className={`mt-2 space-y-1 ${disabled ? "opacity-50" : ""}`}>
                    <Label className="text-xs text-cyan-300/80">Subgroup</Label>
                    <Select
                      value={achievement.subSectionId || "none"}
                      disabled={disabled}
                      onValueChange={(v) =>
                        setAchievement({
                          ...achievement,
                          subSectionId: v === "none" ? null : v,
                        })
                      }
                    >
                      <SelectTrigger
                        className="bg-black/50 border-white/10 h-8 text-sm"
                        data-testid="edit-achievement-subgroup-select"
                      >
                        <SelectValue placeholder={disabled ? "Not available" : "Ungrouped"} />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0A0A0A] border-white/10">
                        <SelectItem value="none">Ungrouped</SelectItem>
                        {[...subs]
                          .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
                          .map((sub) => (
                            <SelectItem key={sub.id} value={sub.id}>
                              {sub.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    {hint && <p className="text-[11px] text-white/40">{hint}</p>}
                  </div>
                );
              })()}
            </div>
          )}

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              value={achievement.description || ""}
              onChange={(e) => setAchievement({ ...achievement, description: e.target.value })}
              className="bg-black/50 border-white/10"
            />
          </div>

          {/* Trophy Type for PlayStation */}
          {platform === "playstation" ? (
            <div className="space-y-2">
              <Label>Trophy Type</Label>
              <Select
                value={achievement.trophyType || ""}
                onValueChange={(v) => {
                  const trophy = TROPHY_TYPES.find(t => t.value === v);
                  setAchievement({ 
                    ...achievement, 
                    trophyType: v,
                    score: trophy?.score || achievement.score 
                  });
                }}
              >
                <SelectTrigger className="bg-black/50 border-white/10">
                  <SelectValue placeholder="Select trophy type" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  {TROPHY_TYPES.map((trophy) => (
                    <SelectItem key={trophy.value} value={trophy.value}>
                      <span style={{ color: trophy.color }}>●</span> {trophy.label} ({trophy.score} XP)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="space-y-2">
              <Label>Score</Label>
              <Input
                type="number"
                value={achievement.score}
                onChange={(e) => setAchievement({ ...achievement, score: parseInt(e.target.value) || 0 })}
                className="bg-black/50 border-white/10"
              />
            </div>
          )}

          <div className="space-y-2">
            <Label>Image URL</Label>
            <Input
              value={achievement.imageUrl || ""}
              onChange={(e) => setAchievement({ ...achievement, imageUrl: e.target.value })}
              className="bg-black/50 border-white/10"
            />
          </div>

          {tags.length > 0 && (
            <div className="space-y-2">
              <Label>Tags</Label>
              <div className="flex flex-wrap gap-2">
                {tags
                  .filter((tag) => tag.name?.toLowerCase() !== "incomplete sync")
                  .sort((a, b) => (a?.name || "").localeCompare(b?.name || ""))
                  .map((tag) => {
                    const color = getTagColor(tag);
                    return (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() => toggleTag(tag.id, true)}
                        className={`px-2 py-1 rounded text-xs transition-all ${
                          achievement.tags?.includes(tag.id)
                            ? "ring-2 ring-white"
                            : "opacity-60 hover:opacity-100"
                        }`}
                        style={{ backgroundColor: color }}
                      >
                        {tag.name}
                      </button>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Secret Achievement toggle — when ON the achievement's name,
              description, and image stay hidden in the game's achievement
              list until the user unlocks it. Honours the global Secret
              settings in Profile → Settings. Per-user override sits on the
              user's achievement record. */}
          <label
            className="flex items-start gap-3 p-3 rounded-md border border-amber-500/20 bg-amber-500/5 cursor-pointer hover:border-amber-500/30 transition-colors"
            data-testid="edit-achievement-secret-toggle"
          >
            <input
              type="checkbox"
              checked={!!achievement.isSecret}
              onChange={(e) => setAchievement({ ...achievement, isSecret: e.target.checked })}
              className="mt-0.5 w-4 h-4 accent-amber-400"
            />
            <div className="min-w-0">
              <div className="text-sm text-white font-medium">Secret achievement</div>
              <p className="text-xs text-white/50">
                Hide the name, description and/or image while this achievement is still locked.
                What stays hidden is controlled by your <span className="text-amber-400/80">Settings → Secret Achievements</span>.
              </p>
            </div>
          </label>

          {/* Per Character — magenta counterpart to Secret. When on, the user
              can log per-character unlocks on this achievement from the game
              info page, without affecting score/stats. Overlay from the MDB
              still wins if the achievement is already marked per-character
              at the master level. */}
          <label
            className="flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors"
            style={{
              borderColor: "rgba(225, 29, 116, 0.2)",
              background: "rgba(225, 29, 116, 0.05)",
            }}
            data-testid="edit-achievement-per-character-toggle"
          >
            <input
              type="checkbox"
              checked={!!achievement.isPerCharacter}
              onChange={(e) => setAchievement({ ...achievement, isPerCharacter: e.target.checked })}
              className="mt-0.5 w-4 h-4"
              style={{ accentColor: "#e11d74" }}
            />
            <div className="min-w-0">
              <div className="text-sm text-white font-medium">Per Character</div>
              <p className="text-xs text-white/50">
                Users can log multiple unlocks — one per character note on the game. Only the primary
                (synced or manual) unlock counts toward score and stats.
              </p>
            </div>
          </label>

          <Button onClick={onSave} className="w-full bg-white text-black hover:bg-gray-200">
            Save Changes
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Timestamp Dialog
export function TimestampDialog({
  open,
  onOpenChange,
  achievement,
  setAchievement,
  onSave,
  gameReleaseDate,
}) {
  const [dateText, setDateText] = useState("");
  const [dateError, setDateError] = useState("");
  
  const minValidDate = getMinValidDate(gameReleaseDate);
  const today = new Date();
  
  // Sync dateText with achievement.earnedAt when dialog opens or achievement changes
  useEffect(() => {
    if (achievement?.earnedAt) {
      try {
        setDateText(format(new Date(achievement.earnedAt), "MM/dd/yyyy"));
        setDateError("");
      } catch {
        setDateText("");
      }
    } else {
      setDateText("");
    }
  }, [achievement?.earnedAt, open]);
  
  if (!achievement) return null;

  const validateAndSetDate = (date) => {
    if (!date) {
      setDateError("");
      setAchievement({ ...achievement, earnedAt: null });
      return;
    }
    
    if (date > today) {
      setDateError("Cannot set unlock date in the future");
      return;
    }
    
    if (date < minValidDate) {
      setDateError(`Date cannot be before ${format(minValidDate, "MMM d, yyyy")} (1 year before game release)`);
      return;
    }
    
    setDateError("");
    setAchievement({ ...achievement, earnedAt: date });
  };

  const handleDateTextChange = (val) => {
    const formatted = formatDateInput(val);
    setDateText(formatted);
    // Try to parse when it looks like a complete date (MM/DD/YYYY format)
    if (formatted.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      const [month, day, year] = formatted.split('/');
      const parsed = new Date(`${year}-${month}-${day}T12:00:00`);
      if (!isNaN(parsed.getTime())) {
        validateAndSetDate(parsed);
      }
    } else if (formatted === "") {
      setDateError("");
      setAchievement({ ...achievement, earnedAt: null });
    }
  };

  const handleSave = () => {
    // Final validation before saving
    if (achievement.earnedAt) {
      const date = new Date(achievement.earnedAt);
      if (date > today) {
        setDateError("Cannot set unlock date in the future");
        return;
      }
      if (date < minValidDate) {
        setDateError(`Date cannot be before ${format(minValidDate, "MMM d, yyyy")}`);
        return;
      }
    }
    onSave();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Edit Unlock Date & Time</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label>Unlock Date</Label>
            <div className="flex gap-2">
              <Input
                type="text"
                value={dateText}
                onChange={(e) => handleDateTextChange(e.target.value)}
                className="bg-black/50 border-white/10 flex-1"
                placeholder="MM/DD/YYYY (e.g., 03/15/2024)"
                maxLength={10}
              />
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="icon" className="border-white/10 bg-black/50 shrink-0">
                    <Calendar className="w-4 h-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-[#0A0A0A] border-white/10">
                  <CalendarWithYear
                    selected={achievement.earnedAt ? new Date(achievement.earnedAt) : undefined}
                    onSelect={(date) => {
                      validateAndSetDate(date);
                      if (date && isValidUnlockDate(date, gameReleaseDate)) {
                        setDateText(format(date, "MM/dd/yyyy"));
                      }
                    }}
                    fromYear={minValidDate.getFullYear()}
                    toYear={new Date().getFullYear()}
                    disabled={(date) => date > today || date < minValidDate}
                  />
                </PopoverContent>
              </Popover>
            </div>
            <p className="text-xs text-white/50">
              Type date as MM/DD/YYYY or click calendar icon to pick
            </p>
          </div>

          <div className="space-y-2">
            <Label>Unlock Time (optional)</Label>
            <Input
              type="text"
              value={achievement.earnedTime || ""}
              onChange={(e) => {
                const formatted = formatTimeInput(e.target.value);
                setAchievement({ ...achievement, earnedTime: formatted });
              }}
              className="bg-black/50 border-white/10"
              placeholder="HH:MM (e.g., 14:30 for 2:30 PM)"
              maxLength={5}
            />
            <p className="text-xs text-white/50">
              Enter time in 24-hour format. Leave blank to show only the date.
            </p>
            {achievement.earnedTime && (
              <div className="p-2 bg-green-500/10 border border-green-500/20 rounded">
                {/^\d{1,2}:\d{2}$/.test(achievement.earnedTime) ? (
                  <p className="text-sm text-green-400">
                    Time will display as: <strong>{formatTime12Hour(achievement.earnedTime)}</strong>
                  </p>
                ) : (
                  <p className="text-sm text-amber-400">
                    Please enter a valid time in HH:MM format (e.g., 14:30)
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
            <Checkbox
              id="unlocked-offline"
              checked={achievement.unlockedOffline || false}
              onCheckedChange={(checked) => setAchievement({ ...achievement, unlockedOffline: checked })}
              className="border-amber-500/50 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
            />
            <Label htmlFor="unlocked-offline" className="text-sm cursor-pointer text-amber-200">
              Unlocked Offline
            </Label>
          </div>
          <p className="text-xs text-white/40 -mt-2">
            Check this if the achievement was unlocked while offline. It will be noted on the achievement.
          </p>

          {dateError && (
            <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
              <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
              <p className="text-sm text-red-400">{dateError}</p>
            </div>
          )}

          <Button onClick={handleSave} className="w-full bg-white text-black hover:bg-gray-200" disabled={!!dateError}>
            Save Timestamp
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Game Info Dialog
export function GameInfoDialog({
  open,
  onOpenChange,
  game,
  setGame,
  platforms,
  systems,
  genreInput,
  setGenreInput,
  addGenre,
  removeGenre,
  onSave,
  onToggleNoAchievements,
}) {
  // Local state for multi-system selection
  const [selectedSystems, setSelectedSystems] = React.useState(game?.systems || (game?.system ? [game.system] : []));
  
  // Update local state when game changes
  React.useEffect(() => {
    setSelectedSystems(game?.systems || (game?.system ? [game.system] : []));
  }, [game?.systems, game?.system]);
  
  const handleSystemToggle = (systemName) => {
    const newSystems = selectedSystems.includes(systemName)
      ? selectedSystems.filter(s => s !== systemName)
      : [...selectedSystems, systemName];
    setSelectedSystems(newSystems);
    setGame({ 
      ...game, 
      systems: newSystems,
      system: newSystems.length > 0 ? newSystems[0] : null 
    });
  };
  
  if (!game) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-lg max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Edit Game</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-4 overflow-y-auto max-h-[65vh] pr-2">
          {/* Basic Info Section */}
          <div className="space-y-4 p-3 bg-white/5 rounded-lg">
            <h3 className="text-sm font-medium text-white/70 uppercase tracking-wider">Basic Info</h3>
            
            <div className="space-y-2">
              <Label>Game Name</Label>
              <Input
                value={game.name || ""}
                onChange={(e) => setGame({ ...game, name: e.target.value })}
                className="bg-black/50 border-white/10"
              />
            </div>

            <div className="space-y-2">
              <Label>Platform</Label>
              <Select
                value={game.platform}
                onValueChange={(value) => {
                  setGame({ ...game, platform: value, system: "", systems: [] });
                  setSelectedSystems([]);
                }}
              >
                <SelectTrigger className="bg-black/50 border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  {platforms.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      <span style={{ color: p.color }}>{p.name}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Systems (select all that apply)</Label>
              <div className="flex flex-wrap gap-2 p-3 bg-black/30 rounded-lg max-h-32 overflow-y-auto">
                {(systems[game.platform] || []).length === 0 ? (
                  <span className="text-white/40 text-sm">No systems available for this platform</span>
                ) : (
                  (systems[game.platform] || []).map((sys) => (
                    <label 
                      key={sys.id || sys.name}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm cursor-pointer transition-colors ${
                        selectedSystems.includes(sys.name)
                          ? "bg-blue-500/30 text-blue-400 border border-blue-500/50"
                          : "bg-white/5 text-white/60 border border-white/10 hover:bg-white/10"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedSystems.includes(sys.name)}
                        onChange={() => handleSystemToggle(sys.name)}
                        className="sr-only"
                      />
                      {sys.name}
                    </label>
                  ))
                )}
              </div>
              {selectedSystems.length > 0 && (
                <p className="text-white/40 text-xs">
                  Selected: {selectedSystems.join(", ")}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Release Date</Label>
              <Input
                type="date"
                value={game.releaseDate || ""}
                onChange={(e) => setGame({ ...game, releaseDate: e.target.value })}
                className="bg-black/50 border-white/10"
              />
            </div>
            
            {/* No Achievements checkbox */}
            <div className="flex items-center gap-3 pt-2">
              <Checkbox 
                id="no-achievements"
                checked={game.hasNoAchievements || false}
                onCheckedChange={(checked) => {
                  setGame({ ...game, hasNoAchievements: checked });
                  if (onToggleNoAchievements) {
                    // Will be called on save
                  }
                }}
              />
              <label htmlFor="no-achievements" className="text-sm text-white/70 cursor-pointer">
                No Achievements (excludes from Library Maintenance)
              </label>
            </div>
          </div>

          {/* Box Art Section */}
          <div className="space-y-4 p-3 bg-white/5 rounded-lg">
            <h3 className="text-sm font-medium text-white/70 uppercase tracking-wider">Box Art</h3>
            
            <div className="flex items-start gap-4">
              {game.boxArtUrl ? (
                <img
                  src={game.boxArtUrl}
                  alt={game.name}
                  className="w-20 h-28 rounded object-contain bg-black/50 shrink-0"
                />
              ) : (
                <div className="w-20 h-28 rounded bg-black/30 flex items-center justify-center shrink-0">
                  <Gamepad2 className="w-8 h-8 text-white/20" />
                </div>
              )}
              <div className="flex-1 space-y-2">
                <Input
                  placeholder="Image URL"
                  value={game.boxArtUrl || ""}
                  onChange={(e) => setGame({ ...game, boxArtUrl: e.target.value })}
                  className="bg-black/50 border-white/10"
                />
                {game.boxArtUrl && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-400 hover:text-red-300 h-8"
                    onClick={() => setGame({ ...game, boxArtUrl: "" })}
                  >
                    Remove Image
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Details Section */}
          <div className="space-y-4 p-3 bg-white/5 rounded-lg">
            <h3 className="text-sm font-medium text-white/70 uppercase tracking-wider">Details</h3>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Publisher</Label>
                <Input
                  value={game.publisher || ""}
                  onChange={(e) => setGame({ ...game, publisher: e.target.value })}
                  className="bg-black/50 border-white/10"
                  placeholder="e.g., Microsoft"
                />
              </div>

              <div className="space-y-2">
                <Label>Developer</Label>
                <Input
                  value={game.developer || ""}
                  onChange={(e) => setGame({ ...game, developer: e.target.value })}
                  className="bg-black/50 border-white/10"
                  placeholder="e.g., Bungie"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Genres</Label>
              <div className="flex gap-2">
                <Input
                  value={genreInput}
                  onChange={(e) => setGenreInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addGenre())}
                  className="bg-black/50 border-white/10 flex-1"
                  placeholder="Add genre..."
                />
                <Button type="button" onClick={addGenre} size="sm" className="bg-purple-600 hover:bg-purple-700">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {game.genres?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {game.genres.map((genre, i) => (
                    <span key={i} className="px-2 py-1 text-xs bg-purple-500/20 text-purple-300 rounded-full flex items-center gap-1">
                      {genre}
                      <button onClick={() => removeGenre(genre)} className="hover:text-white">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Special Hardware</Label>
                <Input
                  value={game.specialHardware || ""}
                  onChange={(e) => setGame({ ...game, specialHardware: e.target.value })}
                  className="bg-black/50 border-white/10"
                  placeholder="e.g., Kinect, VR"
                />
              </div>

              <div className="space-y-2">
                <Label>Completion Est.</Label>
                <Input
                  value={game.completionEstimate || ""}
                  onChange={(e) => setGame({ ...game, completionEstimate: e.target.value })}
                  className="bg-black/50 border-white/10"
                  placeholder="e.g., 20-30 hours"
                />
              </div>
            </div>
          </div>

          <Button onClick={onSave} className="w-full bg-white text-black hover:bg-gray-200">
            Save Changes
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Add Note Dialog

// ==================== Checklist item anchor helpers ====================

// Compact hover-card content for a character or achievement, shown when the
// user hovers over an anchored checklist item's portrait or label chip.
function ChecklistEntityHover({ kind, entity, fallback, children }) {
  const displayName = kind === "character"
    ? (entity?.title || fallback?.entityName || "Unnamed")
    : (entity?.name || fallback?.entityName || "Unnamed");
  const img = kind === "character"
    ? ((entity?.images && entity.images[0]) || fallback?.entityImageUrl || null)
    : (entity?.imageUrl || fallback?.entityImageUrl || null);
  const stats = kind === "character" ? (entity?.statistics || []).slice(0, 4) : [];
  const accent = kind === "character" ? "text-pink-300" : "text-yellow-300";
  return (
    <HoverCard openDelay={200} closeDelay={80}>
      <HoverCardTrigger asChild>
        <span>{children}</span>
      </HoverCardTrigger>
      <HoverCardContent className="w-64 bg-zinc-900 border-zinc-700 text-zinc-200 p-3" side="top">
        <div className="flex items-start gap-2">
          {img && (
            <img src={img} alt="" className="w-14 h-14 rounded object-cover flex-shrink-0" />
          )}
          <div className="min-w-0 flex-1">
            <div className={`text-sm font-semibold truncate ${accent}`}>{displayName}</div>
            {kind === "character" && entity?.text && (
              <div className="text-[11px] text-zinc-400 line-clamp-3 mt-0.5">{entity.text}</div>
            )}
            {kind === "achievement" && (
              <div className="text-[10px] flex items-center gap-1.5 mt-0.5">
                {entity?.isUnlocked ? (
                  <span className="text-emerald-400">Unlocked</span>
                ) : (
                  <span className="text-zinc-500">Locked</span>
                )}
                {typeof entity?.score === "number" && entity.score > 0 && (
                  <span className="text-zinc-400">· {entity.score}</span>
                )}
              </div>
            )}
          </div>
        </div>
        {kind === "achievement" && entity?.description && (
          <div className="text-[11px] text-zinc-400 mt-2 line-clamp-4">{entity.description}</div>
        )}
        {kind === "character" && stats.length > 0 && (
          <div className="mt-2 grid grid-cols-2 gap-1">
            {stats.map(s => (
              <div key={s.id || s.name} className="flex justify-between gap-1 text-[10px] bg-black/30 rounded px-1.5 py-0.5">
                <span className="text-pink-300/80 truncate">{s.name}</span>
                <span className="text-zinc-200 tabular-nums truncate">{String(s.value ?? "")}</span>
              </div>
            ))}
          </div>
        )}
      </HoverCardContent>
    </HoverCard>
  );
}

// Paperclip button + Popover picker to attach a character or achievement
// to a checklist item, plus an inline chip that shows the current anchor
// (with a Clear action). Positions on the left of the item text — matches
// the "left side" placement the user requested.
function ChecklistItemAnchor({ item, onChange, characters = [], achievements = [] }) {
  const [open, setOpen] = useState(false);
  const [kindChoice, setKindChoice] = useState(null);

  const clear = (e) => {
    e?.stopPropagation();
    onChange({ entityType: null, entityId: null, entityName: null, entityImageUrl: null });
  };

  const select = (kind, entity) => {
    const isChar = kind === "character";
    const name = isChar ? (entity.title || "Unnamed") : (entity.name || "Unnamed");
    const img = isChar ? ((entity.images && entity.images[0]) || null) : (entity.imageUrl || null);
    onChange({
      entityType: kind,
      entityId: entity.id,
      entityName: name,
      entityImageUrl: img,
    });
    setOpen(false);
    setKindChoice(null);
  };

  // Live entity lookup for hover card (in case user edited character/achievement
  // metadata since the anchor was created).
  const liveEntity = item.entityType === "character"
    ? characters.find(c => c.id === item.entityId) || null
    : item.entityType === "achievement"
      ? achievements.find(a => a.id === item.entityId) || null
      : null;

  const items = kindChoice === "character" ? characters : kindChoice === "achievement" ? achievements : [];

  return (
    <div className="flex flex-col items-center gap-1 flex-shrink-0">
      {item.entityType ? (
        <ChecklistEntityHover kind={item.entityType} entity={liveEntity} fallback={item}>
          <div className="relative">
            {item.entityImageUrl ? (
              <img
                src={item.entityImageUrl}
                alt=""
                className="w-10 h-10 rounded object-cover border border-white/10"
              />
            ) : (
              <div className={`w-10 h-10 rounded flex items-center justify-center border border-white/10 ${item.entityType === "character" ? "bg-pink-500/10 text-pink-300" : "bg-yellow-500/10 text-yellow-300"}`}>
                {item.entityType === "character" ? <UserCircle2 className="w-5 h-5" /> : <Trophy className="w-5 h-5" />}
              </div>
            )}
            <button
              type="button"
              onClick={clear}
              className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-zinc-800 border border-zinc-600 text-red-400 hover:text-red-300 hover:bg-zinc-700 flex items-center justify-center"
              title="Remove attachment"
            >
              <X className="w-2.5 h-2.5" />
            </button>
          </div>
        </ChecklistEntityHover>
      ) : (
        <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setKindChoice(null); }}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="w-10 h-10 rounded border border-dashed border-white/15 text-white/30 hover:text-white/70 hover:border-white/40 flex items-center justify-center transition-colors"
              title="Attach a character or achievement"
              data-testid={`checklist-item-anchor-${item.id}`}
            >
              <Paperclip className="w-4 h-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent side="right" align="start" className="bg-zinc-800 border border-zinc-700 text-zinc-200 p-1.5 w-[240px] max-w-none shadow-xl rounded-lg">
            <div className="max-h-[280px] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              {!kindChoice ? (
                <>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wider px-1 pb-1">Attach to</div>
                  <button
                    type="button"
                    onClick={() => setKindChoice("character")}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs hover:bg-zinc-700 text-pink-300"
                  >
                    <UserCircle2 className="w-3.5 h-3.5" /> Character…
                  </button>
                  <button
                    type="button"
                    onClick={() => setKindChoice("achievement")}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs hover:bg-zinc-700 text-yellow-300"
                  >
                    <Trophy className="w-3.5 h-3.5" /> Achievement…
                  </button>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between px-1 pb-1">
                    <span className="text-[10px] text-zinc-500 uppercase tracking-wider">
                      {kindChoice === "character" ? "Character" : "Achievement"}
                    </span>
                    <button type="button" onClick={() => setKindChoice(null)} className="text-[10px] text-zinc-500 hover:text-zinc-300">
                      ← Back
                    </button>
                  </div>
                  {items.length === 0 ? (
                    <p className="text-xs text-zinc-500 px-2 py-2 leading-snug">
                      {kindChoice === "character" ? "No character notes on this game yet. Add one first." : "No achievements found for this game."}
                    </p>
                  ) : (
                    items.map(it => {
                      const label = kindChoice === "character" ? (it.title || "Unnamed") : (it.name || "Unnamed");
                      const img = kindChoice === "character"
                        ? ((it.images && it.images[0]) || null)
                        : (it.imageUrl || null);
                      return (
                        <button
                          key={it.id}
                          type="button"
                          onClick={() => select(kindChoice, it)}
                          className="w-full flex items-center gap-2 px-1.5 py-1 rounded text-left hover:bg-zinc-700 transition-colors"
                        >
                          {img ? (
                            <img src={img} alt="" className="w-7 h-7 rounded object-cover flex-shrink-0" />
                          ) : (
                            <div className="w-7 h-7 rounded bg-zinc-700 flex items-center justify-center flex-shrink-0">
                              {kindChoice === "character"
                                ? <UserCircle2 className="w-4 h-4 text-zinc-500" />
                                : <Trophy className="w-4 h-4 text-zinc-500" />}
                            </div>
                          )}
                          <span className="text-xs text-zinc-200 flex-1 truncate">{label}</span>
                        </button>
                      );
                    })
                  )}
                </>
              )}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}

export function AddNoteDialog({
  open,
  onOpenChange,
  noteText,
  setNoteText,
  onSave,
  checklistTemplates = [],
  onSaveChecklist,
  editingNote = null,
  initialNoteType = "note",
  characters = [],
  achievements = [],
}) {
  const [noteType, setNoteType] = useState(initialNoteType);
  const [noteTitle, setNoteTitle] = useState("");
  const [isPrivate, setIsPrivate] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [checklistItems, setChecklistItems] = useState([]);
  const [newItemText, setNewItemText] = useState("");
  const [checklistDescription, setChecklistDescription] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const textareaRef = React.useRef(null);

  // Reset/populate state when dialog opens/closes
  useEffect(() => {
    if (open && editingNote) {
      setNoteType(editingNote.noteType || "note");
      setNoteTitle(editingNote.title || "");
      setIsPrivate(editingNote.isPrivate || false);
      setNoteText(editingNote.text || "");
      setImageUrl(editingNote.imageUrl || "");
      if (editingNote.noteType === "checklist") {
        setChecklistItems(editingNote.checklistItems || []);
        setChecklistDescription(editingNote.description || "");
      }
    } else if (open && !editingNote) {
      setNoteType(initialNoteType);
      setImageUrl("");
    } else if (!open) {
      setNoteType("note");
      setNoteTitle("");
      setIsPrivate(false);
      setSelectedTemplate(null);
      setChecklistItems([]);
      setNewItemText("");
      setChecklistDescription("");
      setImageUrl("");
    }
  }, [open, editingNote, setNoteText, initialNoteType]);

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setNoteText(template.name);
    const base = Date.now();
    setChecklistItems(template.items.map((item, idx) => ({
      id: `temp-${idx}`,
      text: item.text,
      description: item.description || "",
      imageUrl: null,
      checked: false,
      addedAt: new Date(base + idx).toISOString()
    })));
  };

  const addChecklistItem = () => {
    if (!newItemText.trim()) return;
    setChecklistItems([...checklistItems, {
      id: `item-${Date.now()}`,
      text: newItemText.trim(),
      description: "",
      imageUrl: null,
      checked: false,
      addedAt: new Date().toISOString()
    }]);
    setNewItemText("");
  };

  const removeChecklistItem = (itemId) => {
    setChecklistItems(checklistItems.filter(item => item.id !== itemId));
  };

  const updateChecklistItem = (itemId, field, value) => {
    setChecklistItems(checklistItems.map(item => 
      item.id === itemId ? { ...item, [field]: value } : item
    ));
  };

  const handleSave = () => {
    if (noteType === "note") {
      onSave(noteText, noteTitle, isPrivate, { imageUrl: imageUrl.trim() || null });
    } else {
      const base = Date.now();
      const stampedItems = checklistItems.map((item, idx) => ({
        ...item,
        addedAt: item.addedAt || new Date(base + idx).toISOString()
      }));
      onSaveChecklist?.({
        text: noteText || "Untitled Checklist",
        title: noteTitle || null,
        description: checklistDescription.trim() || null,
        noteType: "checklist",
        isPrivate,
        checklistItems: stampedItems,
        templateId: selectedTemplate?.id
      });
    }
    onOpenChange(false);
  };

  // Bold/Italic formatting helpers
  const wrapSelection = (wrapper) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const text = noteText || "";
    const selected = text.substring(start, end);
    const before = text.substring(0, start);
    const after = text.substring(end);
    if (selected) {
      const newText = before + wrapper + selected + wrapper + after;
      setNoteText(newText);
      setTimeout(() => {
        ta.focus();
        ta.selectionStart = start + wrapper.length;
        ta.selectionEnd = end + wrapper.length;
      }, 0);
    } else {
      const newText = before + wrapper + wrapper + after;
      setNoteText(newText);
      setTimeout(() => {
        ta.focus();
        ta.selectionStart = start + wrapper.length;
        ta.selectionEnd = start + wrapper.length;
      }, 0);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            {noteType === "note" ? <StickyNote className="w-5 h-5" /> : <ListTodo className="w-5 h-5" />}
            {editingNote ? "Edit" : "Add"} {noteType === "note" ? "Note" : "Checklist"}
          </DialogTitle>
        </DialogHeader>
        
        {/* Note Type Tabs - hidden when type is pre-selected from dropdown */}
        {!editingNote && !initialNoteType && (
          <Tabs value={noteType} onValueChange={setNoteType} className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-white/5">
              <TabsTrigger value="note" className="data-[state=active]:bg-white/10">
                <StickyNote className="w-4 h-4 mr-2" />
                Note
              </TabsTrigger>
              <TabsTrigger value="checklist" className="data-[state=active]:bg-white/10">
                <ListTodo className="w-4 h-4 mr-2" />
                Checklist
              </TabsTrigger>
            </TabsList>
          </Tabs>
        )}

        <div className="space-y-4 pt-2">
          {/* Title field — notes only. Checklists have their own "Checklist
              Title" field below (bound to noteText), so showing this here too
              created a confusing second, non-functional title box. */}
          {noteType === "note" && (
            <div className="space-y-1">
              <Label className="text-white/70 text-sm">Title <span className="text-white/40 text-xs font-normal">(optional)</span></Label>
              <Input
                value={noteTitle}
                onChange={(e) => setNoteTitle(e.target.value)}
                className="bg-black/50 border-white/10"
                placeholder="e.g., Walkthrough Notes"
                data-testid="note-title-input"
              />
            </div>
          )}

          {noteType === "note" ? (
            <>
              {/* Formatting toolbar */}
              <div className="flex items-center gap-1 -mb-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-white/50 hover:text-white hover:bg-white/10"
                  onClick={() => wrapSelection("**")}
                  title="Bold (wrap with **)"
                >
                  <Bold className="w-3.5 h-3.5" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-white/50 hover:text-white hover:bg-white/10"
                  onClick={() => wrapSelection("*")}
                  title="Italic (wrap with *)"
                >
                  <Italic className="w-3.5 h-3.5" />
                </Button>
                <span className="text-white/20 text-xs ml-2">Use **bold** and *italic*</span>
              </div>
              <Textarea
                ref={textareaRef}
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                className="bg-black/50 border-white/10 min-h-[120px]"
                placeholder="Add notes, walkthrough links, or other helpful information..."
                autoFocus
                data-testid="note-text-input"
              />
              {/* Optional hero image (hotlink URL only). Shown at the top of
                  the note when viewing. Not saved as an upload — purely a
                  reference to an externally hosted image. */}
              <div className="space-y-1">
                <Label className="text-white/70 text-sm">
                  Image URL <span className="text-white/40 text-xs font-normal">(optional, hotlink only)</span>
                </Label>
                <Input
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  className="bg-black/50 border-white/10"
                  placeholder="https://example.com/image.jpg"
                  data-testid="note-image-url-input"
                />
                {imageUrl.trim() && (
                  <div className="mt-2 p-2 rounded border border-white/10 bg-black/30 inline-block">
                    <img
                      src={imageUrl.trim()}
                      alt="Note preview"
                      className="max-w-[120px] max-h-[120px] w-auto h-auto rounded object-contain"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {/* Template Selection */}
              {checklistTemplates.length > 0 && !checklistItems.length && (
                <div className="space-y-2">
                  <Label className="text-white/70 text-sm">Start from template (optional)</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {checklistTemplates.map(template => (
                      <button
                        key={template.id}
                        onClick={() => handleTemplateSelect(template)}
                        className="p-3 bg-white/5 border border-white/10 rounded-lg text-left hover:bg-white/10 transition-colors"
                      >
                        <div className="font-medium text-sm">{template.name}</div>
                        <div className="text-white/50 text-xs mt-1">{template.items.length} items</div>
                      </button>
                    ))}
                  </div>
                  <div className="relative flex items-center py-2">
                    <div className="flex-grow border-t border-white/10"></div>
                    <span className="flex-shrink mx-4 text-white/40 text-xs">or create your own</span>
                    <div className="flex-grow border-t border-white/10"></div>
                  </div>
                </div>
              )}

              {/* Checklist Title */}
              <div className="space-y-2">
                <Label className="text-white/70 text-sm">Checklist Title</Label>
                <Input
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  className="bg-black/50 border-white/10"
                  placeholder="e.g., Collectibles, Missable Achievements..."
                />
              </div>

              {/* Checklist Description */}
              <div className="space-y-2">
                <Label className="text-white/70 text-sm">Description <span className="text-white/40 text-xs font-normal">(optional)</span></Label>
                <Textarea
                  value={checklistDescription}
                  onChange={(e) => setChecklistDescription(e.target.value)}
                  className="bg-black/50 border-white/10 min-h-[60px] resize-none"
                  placeholder="Describe what this checklist is for..."
                  data-testid="checklist-description-input"
                />
              </div>

              {/* Checklist Items */}
              <div className="space-y-2">
                <Label className="text-white/70 text-sm">Items ({checklistItems.length})</Label>
                <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                  {checklistItems.map((item, idx) => (
                    <div key={item.id} className="flex items-start gap-2 p-2 bg-white/5 rounded-lg">
                      <span className="text-white/40 text-xs mt-2">{idx + 1}.</span>
                      <ChecklistItemAnchor
                        item={item}
                        characters={characters}
                        achievements={achievements}
                        onChange={(patch) => {
                          setChecklistItems(checklistItems.map(ci => ci.id === item.id ? { ...ci, ...patch } : ci));
                        }}
                      />
                      <div className="flex-1 space-y-1">
                        <Input
                          value={item.text}
                          onChange={(e) => updateChecklistItem(item.id, "text", e.target.value)}
                          className="bg-black/50 border-white/10 text-sm h-8"
                          placeholder="Item text"
                        />
                        <Input
                          value={item.description}
                          onChange={(e) => updateChecklistItem(item.id, "description", e.target.value)}
                          className="bg-black/50 border-white/10 text-xs h-7"
                          placeholder="Optional description or notes"
                        />
                      </div>
                      <button
                        onClick={() => removeChecklistItem(item.id)}
                        className="text-red-400 hover:text-red-300 p-1"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Add new item */}
                <div className="flex gap-2">
                  <Input
                    value={newItemText}
                    onChange={(e) => setNewItemText(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addChecklistItem()}
                    className="bg-black/50 border-white/10"
                    placeholder="Add an item..."
                  />
                  <Button
                    onClick={addChecklistItem}
                    variant="outline"
                    size="sm"
                    className="border-white/20"
                    disabled={!newItemText.trim()}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          )}

          {/* Private toggle */}
          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              onClick={() => setIsPrivate(!isPrivate)}
              className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border transition-colors ${
                isPrivate
                  ? "bg-red-500/10 border-red-500/30 text-red-400"
                  : "bg-white/5 border-white/10 text-white/40 hover:text-white/60"
              }`}
              data-testid="note-private-toggle"
            >
              {isPrivate ? <Lock className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
              {isPrivate ? "Private" : "Visible when shared"}
            </button>
          </div>

          <Button 
            onClick={handleSave} 
            className="w-full bg-white text-black hover:bg-gray-200"
            disabled={noteType === "note" ? !noteText.trim() : !noteText.trim() && !checklistItems.length}
            data-testid="note-save-btn"
          >
            {editingNote ? "Save Changes" : (noteType === "note" ? "Add Note" : "Create Checklist")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Checklist Display Component (for rendering in notes section)
export function ChecklistDisplay({
  checklist,
  onToggleItem,
  onShare,
  onDelete,
  onEdit,
  characters = [],
  achievements = [],
  headerExtras = null,
  onSavePrefs,
}) {
  const completedCount = checklist.checklistItems?.filter(item => item.checked).length || 0;
  const totalCount = checklist.checklistItems?.length || 0;
  const progressPercent = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
  // Collapsed by default so checklists match the visual rhythm of other
  // note types (text notes / map notes use the same collapsed-by-default
  // pattern). The header chevron and the title toggle expansion.
  const [collapsed, setCollapsed] = useState(true);
  // View-side ordering of items. "saved" keeps the manually-arranged order.
  // Initialized from persisted prefs on the checklist note.
  const [sortMode, setSortMode] = useState(checklist.sortMode || "saved");

  const changeSortMode = (mode) => {
    setSortMode(mode);
    onSavePrefs?.(checklist.id, { sortMode: mode });
  };

  const sortLabels = {
    saved: "Saved order",
    alpha: "Alphabetical",
    info: "Additional info",
    added: "Date added",
  };

  const addedValue = (item, idx) => {
    if (item.addedAt) {
      const t = Date.parse(item.addedAt);
      if (!isNaN(t)) return t;
    }
    const m = /^item-(\d+)/.exec(item.id || "");
    if (m) return parseInt(m[1], 10);
    return idx; // stable fallback to original position
  };

  const sortedItems = useMemo(() => {
    const items = [...(checklist.checklistItems || [])];
    if (sortMode === "alpha") {
      return items.sort((a, b) => (a.text || "").localeCompare(b.text || ""));
    }
    if (sortMode === "info") {
      return items.sort((a, b) => (a.description || "").localeCompare(b.description || ""));
    }
    if (sortMode === "added") {
      return items
        .map((it, idx) => ({ it, key: addedValue(it, idx) }))
        .sort((a, b) => a.key - b.key)
        .map((x) => x.it);
    }
    return items;
  }, [checklist.checklistItems, sortMode]);

  // Optional preference: push checked items to the bottom so remaining
  // (unchecked) items stay grouped at the top. Applied on top of the active
  // sort, preserving each group's sorted order.
  const [checkedToBottom, setCheckedToBottom] = useState(checklist.completedToBottom ?? false);
  const changeCheckedToBottom = (val) => {
    setCheckedToBottom(val);
    onSavePrefs?.(checklist.id, { completedToBottom: val });
  };
  const displayItems = useMemo(() => {
    if (!checkedToBottom) return sortedItems;
    const unchecked = sortedItems.filter((it) => !it.checked);
    const checked = sortedItems.filter((it) => it.checked);
    return [...unchecked, ...checked];
  }, [sortedItems, checkedToBottom]);

  return (
    <div
      className={`bg-[#121212] rounded-xl border border-white/5 hover:border-blue-500/30 transition-colors ${collapsed ? "p-3" : "p-4 space-y-3"}`}
      data-testid={`checklist-card-${checklist.id}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setCollapsed(!collapsed)}
          className="flex-1 min-w-0 text-left"
          data-testid={`checklist-toggle-${checklist.id}`}
        >
          <div className="flex items-center gap-2">
            <ListTodo className="w-4 h-4 text-blue-400 flex-shrink-0" />
            <ChevronRight
              className={`w-3.5 h-3.5 text-white/40 transition-transform flex-shrink-0 ${collapsed ? "" : "rotate-90"}`}
            />
            <span className="font-medium truncate">{checklist.text || "Untitled Checklist"}</span>
          </div>
          {!collapsed && checklist.description && (
            <p className="text-xs text-white/40 mt-1 ml-6">{checklist.description}</p>
          )}
        </button>
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          {/* Compact progress meter, always visible — matches the map note's
              inline `X/N ▬▬▬` pattern so every counted note type shows its
              progress in the same location. */}
          {totalCount > 0 && (
            <div className="flex items-center gap-1.5 mr-1" title={`${completedCount} of ${totalCount} complete`}>
              <span className="text-[10px] text-white/50 tabular-nums">{completedCount}/{totalCount}</span>
              <div className="w-14 h-1 bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-green-500 transition-all duration-300"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>
          )}
          <button
            onClick={() => onShare?.(checklist)}
            className="text-white/50 hover:text-cyan-400 transition-colors p-0.5"
            title="Share as template"
          >
            <Share2 className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => onEdit?.(checklist)}
            className="text-white/50 hover:text-yellow-400 transition-colors p-0.5"
            title="Edit checklist"
            data-testid={`edit-checklist-${checklist.id}`}
          >
            <Edit2 className="w-3.5 h-3.5" />
          </button>
          {headerExtras}
          <button
            onClick={() => onDelete?.(checklist.id)}
            className="text-white/50 hover:text-red-400 transition-colors p-0.5"
            title="Delete checklist"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Progress Bar — visible only when expanded so collapsed checklists
          match the compact one-line look of plain text notes. */}
      {!collapsed && (
        <div className="space-y-1">
          <div className="flex justify-between text-xs text-white/50">
            <span>{completedCount} of {totalCount} completed</span>
            <span>{Math.round(progressPercent)}%</span>
          </div>
          <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-green-500 transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      )}

      {/* Checklist Items — only when expanded */}
      {!collapsed && (
        <div className="space-y-2">
          {totalCount > 1 && (
            <div className="flex items-center justify-between gap-2">
              <label
                className="flex items-center gap-1.5 text-xs text-white/50 hover:text-white/80 transition-colors cursor-pointer select-none"
                data-testid={`checklist-checked-bottom-label-${checklist.id}`}
              >
                <Checkbox
                  checked={checkedToBottom}
                  onCheckedChange={(v) => changeCheckedToBottom(!!v)}
                  className="data-[state=checked]:bg-blue-500 data-[state=checked]:border-blue-500 h-3.5 w-3.5"
                  data-testid={`checklist-checked-bottom-${checklist.id}`}
                />
                Move completed to bottom
              </label>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="flex items-center gap-1.5 text-xs text-white/50 hover:text-white/80 transition-colors px-2 py-1 rounded-md border border-white/10 hover:border-white/20"
                    data-testid={`checklist-sort-trigger-${checklist.id}`}
                  >
                    <ArrowUpDown className="w-3 h-3" />
                    Sort: {sortLabels[sortMode]}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-48 p-1 bg-[#1A1A1A] border-white/10" align="end">
                  {Object.entries(sortLabels).map(([mode, label]) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => changeSortMode(mode)}
                      className={`w-full text-left text-sm px-2.5 py-1.5 rounded-md transition-colors ${
                        sortMode === mode ? "bg-blue-500/20 text-blue-300" : "text-white/70 hover:bg-white/10"
                      }`}
                      data-testid={`checklist-sort-${mode}-${checklist.id}`}
                    >
                      {label}
                    </button>
                  ))}
                </PopoverContent>
              </Popover>
            </div>
          )}
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {displayItems.map((item) => {
              const liveEntity = item.entityType === "character"
                ? characters.find(c => c.id === item.entityId) || null
                : item.entityType === "achievement"
                  ? achievements.find(a => a.id === item.entityId) || null
                  : null;
              return (
              <div
                key={item.id}
                className={`flex items-start gap-3 p-2 rounded-lg transition-colors ${
                  item.checked ? 'bg-green-500/10' : 'bg-white/5 hover:bg-white/10'
                }`}
              >
                <Checkbox
                  checked={item.checked}
                  onCheckedChange={(checked) => onToggleItem?.(checklist.id, item.id, checked)}
                  className="mt-0.5 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                />
                {item.entityType && (
                  <ChecklistEntityHover kind={item.entityType} entity={liveEntity} fallback={item}>
                    <div className="flex-shrink-0">
                      {item.entityImageUrl ? (
                        <img
                          src={item.entityImageUrl}
                          alt=""
                          className="w-10 h-10 rounded object-cover border border-white/10"
                        />
                      ) : (
                        <div className={`w-10 h-10 rounded flex items-center justify-center border border-white/10 ${item.entityType === "character" ? "bg-pink-500/10 text-pink-300" : "bg-yellow-500/10 text-yellow-300"}`}>
                          {item.entityType === "character" ? <UserCircle2 className="w-5 h-5" /> : <Trophy className="w-5 h-5" />}
                        </div>
                      )}
                    </div>
                  </ChecklistEntityHover>
                )}
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${item.checked ? 'line-through text-white/50' : ''}`}>
                    {item.text}
                  </p>
                  {item.description && (
                    <p className="text-xs text-white/40 mt-0.5">{item.description}</p>
                  )}
                  {item.entityName && (
                    <p className={`text-[11px] mt-0.5 truncate ${item.entityType === "character" ? "text-pink-300/80" : "text-yellow-300/80"}`}>
                      {item.entityType === "character" ? "Character: " : "Achievement: "}{item.entityName}
                    </p>
                  )}
                  {item.imageUrl && (
                    <img
                      src={item.imageUrl}
                      alt=""
                      className="mt-2 max-w-[200px] rounded"
                    />
                  )}
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Timestamp — only when expanded */}
      {!collapsed && checklist.createdAt && (
        <p className="text-xs text-white/30 pt-2 border-t border-white/10">
          Created {new Date(checklist.createdAt).toLocaleDateString()}
          {checklist.copiedFrom && " • Copied from community"}
        </p>
      )}
    </div>
  );
}

// Share Checklist Dialog
export function ShareChecklistDialog({
  open,
  onOpenChange,
  checklist,
  onShare,
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("general");
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    if (checklist) {
      setTitle(checklist.text || "");
      setDescription("");
      setCategory("general");
    }
  }, [checklist]);

  const handleShare = async () => {
    setSharing(true);
    try {
      await onShare({
        title,
        description,
        category,
      });
      onOpenChange(false);
    } finally {
      setSharing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Share2 className="w-5 h-5" />
            Share Checklist
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <p className="text-white/60 text-sm">
            Share this checklist as a template that others can copy and use for their own progress tracking.
          </p>

          <div className="space-y-2">
            <Label className="text-white/70">Title</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="bg-black/50 border-white/10"
              placeholder="Give your checklist a descriptive title"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-white/70">Description (optional)</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="bg-black/50 border-white/10 min-h-[80px]"
              placeholder="Add tips or context for others..."
            />
          </div>

          <div className="space-y-2">
            <Label className="text-white/70">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="bg-black/50 border-white/10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1a1a1a] border-white/10">
                <SelectItem value="general">General</SelectItem>
                <SelectItem value="collectibles">Collectibles</SelectItem>
                <SelectItem value="missables">Missable Achievements</SelectItem>
                <SelectItem value="speedrun">Speedrun</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="bg-white/5 rounded-lg p-3">
            <p className="text-white/50 text-xs">
              This checklist has {checklist?.checklistItems?.length || 0} items.
              Others will be able to copy it and track their own progress.
            </p>
          </div>

          <Button
            onClick={handleShare}
            className="w-full bg-blue-600 hover:bg-blue-700"
            disabled={!title.trim() || sharing}
          >
            {sharing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sharing...
              </>
            ) : (
              <>
                <Share2 className="w-4 h-4 mr-2" />
                Share to Community
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Edit Checklist Dialog
export function EditChecklistDialog({
  open,
  onOpenChange,
  checklist,
  onSave,
  characters = [],
  achievements = [],
}) {
  const [title, setTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [checklistItems, setChecklistItems] = useState([]);
  const [newItemText, setNewItemText] = useState("");
  const [saving, setSaving] = useState(false);

  // Initialize state when dialog opens
  useEffect(() => {
    if (open && checklist) {
      setTitle(checklist.text || "");
      setEditDescription(checklist.description || "");
      setChecklistItems(checklist.checklistItems?.map(item => ({
        ...item,
        id: item.id || `item-${Date.now()}-${Math.random()}`
      })) || []);
    }
  }, [open, checklist]);

  const addChecklistItem = () => {
    if (!newItemText.trim()) return;
    setChecklistItems([...checklistItems, {
      id: `item-${Date.now()}`,
      text: newItemText.trim(),
      description: "",
      imageUrl: null,
      checked: false,
      addedAt: new Date().toISOString()
    }]);
    setNewItemText("");
  };

  const removeChecklistItem = (itemId) => {
    setChecklistItems(checklistItems.filter(item => item.id !== itemId));
  };

  const updateChecklistItem = (itemId, field, value) => {
    setChecklistItems(checklistItems.map(item => 
      item.id === itemId ? { ...item, [field]: value } : item
    ));
  };

  const moveItem = (index, direction) => {
    const newItems = [...checklistItems];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newItems.length) return;
    [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
    setChecklistItems(newItems);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const base = Date.now();
      const stampedItems = checklistItems.map((item, idx) => ({
        ...item,
        addedAt: item.addedAt || new Date(base + idx).toISOString()
      }));
      await onSave?.(checklist.id, {
        text: title,
        description: editDescription.trim() || null,
        checklistItems: stampedItems
      });
      onOpenChange(false);
    } catch (err) {
      console.error("Failed to save checklist:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit2 className="w-5 h-5 text-yellow-400" />
            Edit Checklist
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 flex-1 overflow-y-auto pr-1">
          {/* Checklist Title */}
          <div className="space-y-2">
            <Label className="text-white/70 text-sm">Checklist Title</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="bg-black/50 border-white/10"
              placeholder="e.g., Collectibles, Missable Achievements..."
            />
          </div>

          {/* Checklist Description */}
          <div className="space-y-2">
            <Label className="text-white/70 text-sm">Description <span className="text-white/40 text-xs font-normal">(optional)</span></Label>
            <Textarea
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              className="bg-black/50 border-white/10 min-h-[60px] resize-none"
              placeholder="Describe what this checklist is for..."
              data-testid="edit-checklist-description-input"
            />
          </div>

          {/* Checklist Items */}
          <div className="space-y-2">
            <Label className="text-white/70 text-sm">Items ({checklistItems.length})</Label>
            <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
              {checklistItems.map((item, idx) => (
                <div key={item.id} className="flex items-start gap-2 p-2 bg-white/5 rounded-lg group">
                  <div className="flex flex-col gap-0.5">
                    <button
                      onClick={() => moveItem(idx, 'up')}
                      disabled={idx === 0}
                      className="text-white/30 hover:text-white/70 disabled:opacity-20 p-0.5"
                      title="Move up"
                    >
                      <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 15l-6-6-6 6"/>
                      </svg>
                    </button>
                    <span className="text-white/40 text-xs text-center">{idx + 1}</span>
                    <button
                      onClick={() => moveItem(idx, 'down')}
                      disabled={idx === checklistItems.length - 1}
                      className="text-white/30 hover:text-white/70 disabled:opacity-20 p-0.5"
                      title="Move down"
                    >
                      <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M6 9l6 6 6-6"/>
                      </svg>
                    </button>
                  </div>
                  <ChecklistItemAnchor
                    item={item}
                    characters={characters}
                    achievements={achievements}
                    onChange={(patch) => {
                      setChecklistItems(checklistItems.map(ci => ci.id === item.id ? { ...ci, ...patch } : ci));
                    }}
                  />
                  <div className="flex-1 space-y-1">
                    <Input
                      value={item.text}
                      onChange={(e) => updateChecklistItem(item.id, "text", e.target.value)}
                      className="bg-black/50 border-white/10 text-sm h-8"
                      placeholder="Item text"
                    />
                    <Input
                      value={item.description || ""}
                      onChange={(e) => updateChecklistItem(item.id, "description", e.target.value)}
                      className="bg-black/50 border-white/10 text-xs h-7"
                      placeholder="Optional description or notes"
                    />
                  </div>
                  <button
                    onClick={() => removeChecklistItem(item.id)}
                    className="text-red-400 hover:text-red-300 p-1 opacity-50 group-hover:opacity-100"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            {/* Add new item */}
            <div className="flex gap-2 pt-2">
              <Input
                value={newItemText}
                onChange={(e) => setNewItemText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addChecklistItem()}
                className="bg-black/50 border-white/10"
                placeholder="Add a new item..."
              />
              <Button
                onClick={addChecklistItem}
                variant="outline"
                size="sm"
                className="border-white/20"
                disabled={!newItemText.trim()}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        <div className="flex gap-2 pt-4 border-t border-white/10">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="flex-1 border-white/20"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            className="flex-1 bg-yellow-600 hover:bg-yellow-700"
            disabled={!title.trim() || saving}
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Refresh from Database Dialog
export function RefreshDialog({
  open,
  onOpenChange,
  refreshSource,
  setRefreshSource,
  refreshSearch,
  setRefreshSearch,
  refreshResults,
  setRefreshResults,
  selectedRefreshGame,
  setSelectedRefreshGame,
  refreshFields,
  toggleRefreshField,
  refreshLoading,
  onSearch,
  onSelectGame,
  onRefresh,
  masterDbSearched,
  setMasterDbSearched,
}) {
  const [requestOpen, setRequestOpen] = useState(false);
  const handleSearchIGDB = () => {
    setRefreshSource("igdb");
    setRefreshResults([]);
    setSelectedRefreshGame(null);
    // Trigger search with IGDB
    setTimeout(() => onSearch(), 100);
  };

  return (
    <>
    <Dialog open={open} onOpenChange={(isOpen) => {
      onOpenChange(isOpen);
      if (!isOpen) {
        setRefreshResults([]);
        setSelectedRefreshGame(null);
        if (setMasterDbSearched) setMasterDbSearched(false);
      }
    }}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-purple-400" />
            Update Game from Database
          </DialogTitle>
        </DialogHeader>
        
        {/* Source indicator */}
        <div className="mt-2 flex items-center gap-2">
          <span className={`px-2 py-1 rounded text-xs font-medium ${
            refreshSource === "master" 
              ? "bg-green-500/20 text-green-400 border border-green-500/30" 
              : "bg-purple-500/20 text-purple-400 border border-purple-500/30"
          }`}>
            {refreshSource === "master" ? "Master Database" : "IGDB"}
          </span>
          <span className="text-white/40 text-xs">
            {refreshSource === "master" 
              ? "(Your curated data - Primary)" 
              : "(External database - Fallback)"}
          </span>
        </div>
        
        <div className="space-y-4 mt-4">
          {/* Search Input */}
          <div className="space-y-2">
            <Label>Search for game</Label>
            <div className="flex gap-2">
              <Input
                data-testid="refresh-search-input"
                placeholder="Enter game name..."
                value={refreshSearch}
                onChange={(e) => setRefreshSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && onSearch()}
                className="bg-black/50 border-white/10 flex-1"
              />
              <Button
                onClick={onSearch}
                disabled={refreshLoading}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {refreshLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Search className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>
          
          {/* No results in Master DB — offer a support request FIRST, with
              IGDB only as an explicit secondary ("last resort") action. */}
          {masterDbSearched && refreshResults.length === 0 && refreshSource === "master" && !refreshLoading && (
            <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg space-y-3">
              <p className="text-amber-400 text-sm">
                No matches found in the Master Database.
              </p>
              <Button
                onClick={() => setRequestOpen(true)}
                size="sm"
                className="bg-cyan-600 hover:bg-cyan-500 text-white"
                data-testid="refresh-request-game-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                Request this game
              </Button>
              <div className="pt-2 border-t border-white/10">
                <p className="text-white/40 text-xs mb-2">Still can't find it? You can search the external IGDB database as a last resort.</p>
                <Button
                  onClick={handleSearchIGDB}
                  variant="outline"
                  size="sm"
                  className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                  data-testid="refresh-search-igdb-btn"
                >
                  <Database className="w-4 h-4 mr-2" />
                  Search IGDB Instead
                </Button>
              </div>
            </div>
          )}
          
          {/* No results in IGDB either - allow searching again */}
          {masterDbSearched && refreshResults.length === 0 && refreshSource === "igdb" && !refreshLoading && (
            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-red-400 text-sm mb-2">
                No matches found in Master Database or IGDB
              </p>
              <p className="text-white/50 text-xs mb-3">
                Try a different search term (shorter name, remove special characters, check spelling), or request that we add this game.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => {
                    setMasterDbSearched(false);
                    setRefreshResults([]);
                    setRefreshSource("master");
                  }}
                  variant="outline"
                  size="sm"
                  className="border-white/30 text-white/70 hover:bg-white/10"
                >
                  <Search className="w-4 h-4 mr-2" />
                  Try Different Search
                </Button>
                <Button
                  onClick={() => setRequestOpen(true)}
                  variant="outline"
                  size="sm"
                  className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"
                  data-testid="refresh-request-game-btn-igdb"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Request this game
                </Button>
              </div>
            </div>
          )}
          
          {/* Search Results */}
          {refreshResults.length > 0 && !selectedRefreshGame && (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              <Label className="text-white/60">
                Select the correct game ({refreshResults.length} found in {refreshSource === "master" ? "Master Database" : "IGDB"})
              </Label>
              {refreshResults.map((result) => (
                <button
                  key={result.id}
                  onClick={() => onSelectGame(result)}
                  className={`w-full p-3 bg-[#121212] rounded-lg border transition-colors text-left flex gap-3 ${
                    result.source === "master" 
                      ? "border-green-500/20 hover:border-green-500/40" 
                      : "border-white/5 hover:border-purple-500/40"
                  }`}
                >
                  {result.cover_url ? (
                    <img
                      src={result.cover_url}
                      alt={result.name}
                      className="w-12 h-16 object-contain bg-black/50 rounded"
                    />
                  ) : (
                    <div className="w-12 h-16 bg-white/5 rounded flex items-center justify-center">
                      <Gamepad2 className="w-5 h-5 text-white/20" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-white font-medium truncate">{result.name}</p>
                      {/* Release year — extracted from the full release date.
                          Surfaces alongside the region badge so users can
                          tell regional + year variants apart at a glance. */}
                      {(() => {
                        const yr = (result.released || "").match(/(\d{4})/);
                        return yr ? (
                          <span
                            className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-white/10 text-white/70 border border-white/10 font-semibold flex-shrink-0"
                            title={`Released ${result.released}`}
                          >
                            {yr[1]}
                          </span>
                        ) : null;
                      })()}
                      {result.region && (
                        <span
                          className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-400/30 font-semibold flex-shrink-0"
                          title={`Region: ${result.region}`}
                        >
                          {result.region}
                        </span>
                      )}
                    </div>
                    <p className="text-white/50 text-sm">{result.released || "Unknown release"}</p>
                    {/* Developer / Publisher — show both when available so
                        users can distinguish re-releases (often same name
                        and year but different publisher). */}
                    <p className="text-white/40 text-xs truncate">
                      {[
                        result.developers?.join(", "),
                        result.publishers?.join(", "),
                      ]
                        .filter((s) => s && s.trim())
                        .join(" · ") || "Unknown developer"}
                    </p>
                    {/* Genres — compact pills, helps when same title was
                        released under different categorisations. */}
                    {result.genres && result.genres.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {result.genres.slice(0, 3).map((genre, idx) => (
                          <span
                            key={`genre-${idx}`}
                            className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-300/80 border border-purple-400/20"
                          >
                            {genre}
                          </span>
                        ))}
                      </div>
                    )}
                    {/* Platform and System badges */}
                    {(result.platforms?.length > 0 || result.systems?.length > 0) && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {/* Unique platforms — results may carry platforms as
                            plain strings (Master DB) or objects (IGDB), so
                            normalize each to a display string before use. */}
                        {[...new Set((result.platforms || [])
                          .map(p => (typeof p === "string" ? p : (p?.our_platform || p?.name || "")))
                          .filter(Boolean)
                          .map(s => s.toLowerCase()))].map((platform, idx) => (
                          <span 
                            key={`platform-${idx}`}
                            className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                              platform.includes('xbox') 
                                ? 'bg-green-500/20 text-green-400'
                                : platform.includes('playstation') || platform === 'ps4' || platform === 'ps5' || platform === 'ps3'
                                ? 'bg-blue-500/20 text-blue-400'
                                : platform.includes('steam') || platform === 'pc'
                                ? 'bg-gray-500/20 text-gray-400'
                                : platform.includes('nintendo') || platform === 'switch'
                                ? 'bg-red-500/20 text-red-400'
                                : 'bg-white/10 text-white/60'
                            }`}
                          >
                            {platform.charAt(0).toUpperCase() + platform.slice(1)}
                          </span>
                        ))}
                        {/* Systems */}
                        {(result.systems || []).map((system, idx) => (
                          <span 
                            key={`system-${idx}`}
                            className="text-xs px-1.5 py-0.5 rounded bg-white/5 text-white/50"
                          >
                            {system}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  {result.source === "master" && (
                    <span className="text-green-400 text-xs self-start">Master DB</span>
                  )}
                </button>
              ))}
              
              {/* Show IGDB option if showing Master DB results */}
              {refreshSource === "master" && refreshResults.length > 0 && (
                <div className="pt-2 border-t border-white/10">
                  <Button
                    onClick={handleSearchIGDB}
                    variant="ghost"
                    size="sm"
                    className="w-full text-white/40 hover:text-purple-400"
                  >
                    <Database className="w-4 h-4 mr-2" />
                    Not what you&apos;re looking for? Search IGDB instead
                  </Button>
                </div>
              )}
            </div>
          )}
            
            {/* Selected Game */}
            {selectedRefreshGame && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-white/60">Selected Game</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedRefreshGame(null)}
                    className="text-white/40 hover:text-white h-auto py-1"
                  >
                    Change
                  </Button>
                </div>
                <div className={`p-3 bg-[#121212] rounded-lg border ${
                  refreshSource === "igdb" ? "border-purple-500/30" : "border-green-500/30"
                } flex gap-3`}>
                  {(selectedRefreshGame.cover_url || selectedRefreshGame.background_image) ? (
                    <img
                      src={selectedRefreshGame.cover_url || selectedRefreshGame.background_image}
                      alt={selectedRefreshGame.name}
                      className="w-16 h-20 object-contain bg-black/50 rounded"
                    />
                  ) : (
                    <div className="w-16 h-20 bg-white/5 rounded flex items-center justify-center">
                      <Gamepad2 className="w-6 h-6 text-white/20" />
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-white font-medium">{selectedRefreshGame.name}</p>
                      {selectedRefreshGame.source === "master" && (
                        <span className="px-1.5 py-0.5 text-xs bg-green-500/20 text-green-400 rounded">Master DB</span>
                      )}
                    </div>
                    <p className="text-white/50 text-sm">{selectedRefreshGame.released || "Unknown release"}</p>
                    <p className="text-white/40 text-xs">
                      {selectedRefreshGame.developers?.join(", ") || selectedRefreshGame.publishers?.join(", ") || ""}
                    </p>
                  </div>
                </div>
                
                {/* Fields to Update */}
                <div className="space-y-2">
                  <Label>Select fields to update</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { id: "boxArtUrl", label: "Box Art / Cover Image", icon: Image },
                      { id: "releaseDate", label: "Release Date", icon: Calendar },
                      { id: "publisher", label: "Publisher", icon: Gamepad2 },
                      { id: "developer", label: "Developer", icon: Gamepad2 },
                      { id: "genres", label: "Genres", icon: Tag },
                      { id: "completionEstimate", label: "Completion Time", icon: Clock, masterOnly: true },
                      { id: "achievements", label: "Achievements & DLCs", icon: Trophy, masterOnly: true },
                    ].filter(field => !field.masterOnly || selectedRefreshGame?.source === "master" || refreshSource === "master").map((field) => (
                      <button
                        key={field.id}
                        onClick={() => toggleRefreshField(field.id)}
                        className={`p-2 rounded-lg border text-left text-sm flex items-center gap-2 transition-colors ${
                          refreshFields.includes(field.id)
                            ? (selectedRefreshGame?.source === "master" || refreshSource === "master")
                              ? "bg-green-500/20 border-green-500/40 text-green-300"
                              : "bg-purple-500/20 border-purple-500/40 text-purple-300"
                            : "bg-[#121212] border-white/10 text-white/60 hover:border-white/20"
                        }`}
                      >
                        <field.icon className="w-4 h-4" />
                        {field.label}
                      </button>
                    ))}
                  </div>
                </div>
                
                <Button
                  onClick={onRefresh}
                  disabled={refreshLoading || refreshFields.length === 0}
                  data-testid="confirm-refresh-btn"
                  className={`w-full font-bold uppercase tracking-wider ${
                    (selectedRefreshGame?.source === "master" || refreshSource === "master") 
                      ? "bg-green-600 hover:bg-green-700" 
                      : "bg-purple-600 hover:bg-purple-700"
                  }`}
                >
                  {refreshLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Update from {(selectedRefreshGame?.source === "master" || refreshSource === "master") ? "Master DB" : "IGDB"}
                    </>
                  )}
                </Button>
              </div>
            )}
        </div>
      </DialogContent>
      </Dialog>

      {/* Request support for a game when it isn't found in the DB.
          Feeds the MDB Platform Coverage Tracker via POST /api/game-requests. */}
      <GameRequestDialog
        open={requestOpen}
        onOpenChange={setRequestOpen}
        defaultGameName={refreshSearch}
      />
    </>
  );
}
